import { oc } from "@orpc/contract";
import { z } from "zod";
import { DiscountCodeKind } from "./discount-code";
import { Cents } from "./money";
import { AppliedPriceRule, VatBasis } from "./price";
import { ShopPhoto } from "./shop-catalog";

/**
 * The shop's cart (CONTEXT.md, `cart`; spec, "Mand en checkout"): server-side, identified by the
 * `__Host-cart` cookie for a guest and by the customer for a signed-in customer, so it follows them
 * to another device. The backend computes every amount; the shop only shows them.
 *
 * - The cart is made by the first line added; until then `GET` answers an empty cart and sets no
 *   cookie.
 * - Every answer is the whole cart, priced for whoever asks (guest or customer), so the shop
 *   replaces what it shows with it.
 * - When a guest with a cart signs in, the next cart request merges the guest cart into the
 *   customer's (quantities add up, within availability) and the cart cookie is cleared.
 * - A guest cart is kept for 30 days after its last change; a customer's cart is kept.
 * - Shipping follows the shipping rule for the delivery country and the customer's group (else
 *   Consument's rule for that country), free from its threshold on the goods after discount.
 * - One discount code at a time. The cart checks and shows it; it is counted as used only when the
 *   order is placed. A code that stops applying (expired, used up, a line removed) drops off with a
 *   notice.
 *
 * Errors come back as `{ _tag, message }` with an HTTP status; the tags are named per procedure.
 * An invalid body answers 400.
 */

// ---------------------------------------------------------------------------------------------
// Fields

/** The countries the shop delivers to. */
export const DeliveryCountry = z.enum(["NL", "BE"]);
export type DeliveryCountry = z.infer<typeof DeliveryCountry>;

/**
 * The most units one cart line may hold (#218): an unpaid order reserves its stock, so one order
 * may not hold back a product's whole stock.
 */
export const CART_QUANTITY_MAX = 50;

/** A number of units in the cart. */
export const CartQuantity = z.number().int().min(1).max(CART_QUANTITY_MAX);

/**
 * - `consumer`: prices include VAT (guests and consumers, NL and BE);
 * - `business`: prices exclude VAT, VAT on top (an approved business customer);
 * - `reverseCharge`: prices exclude VAT, no VAT charged: "btw verlegd" (an approved business
 *   delivering to Belgium with a valid VAT id).
 */
export const VatTreatment = z.enum(["consumer", "business", "reverseCharge"]);
export type VatTreatment = z.infer<typeof VatTreatment>;

/** An amount on both sides of VAT. */
export const VatAmounts = z.object({ excl: Cents, vat: Cents, incl: Cents });
export type VatAmounts = z.infer<typeof VatAmounts>;

/** One row of the VAT table: the lines at one rate, summed. Under reverse charge, one row at 0. */
export const CartVatRate = VatAmounts.extend({ vatRate: z.number().int() });
export type CartVatRate = z.infer<typeof CartVatRate>;

export const CartLine = z.object({
	variantId: z.string(),
	productId: z.string(),
	productName: z.string(),
	productSlug: z.string(),
	sku: z.string(),
	/** The variant's option values, in the product's option order: `[{ option: "Formaat", value: "XXL" }]`. */
	options: z.array(z.object({ option: z.string(), value: z.string() })),
	quantity: z.number().int(),
	/** How many can still be sold now. Nothing is reserved until the order. */
	available: z.number().int(),
	/**
	 * The quantity is more than is available now: checkout will refuse it until it is lowered. With
	 * nothing available (`available` ≤ 0) the line has just sold out: it keeps its prices but is not
	 * counted in `totals`, the shipping or the discount code.
	 */
	exceedsAvailability: z.boolean(),
	/** The price per unit on the cart's `priceDisplay`, after group and quantity discounts. */
	unitPrice: Cents,
	/** `unitPrice × quantity`, on `priceDisplay`. */
	lineTotal: Cents,
	unitPriceExcl: Cents,
	unitPriceIncl: Cents,
	/** The rules that made the price (`Prices.resolve`). */
	applied: z.array(AppliedPriceRule),
	/** A customer price: final, no quantity discount on top, and no discount code on the line. */
	final: z.boolean(),
	/** The line taxed, after its share of a discount code; `vatRate` is what it charges (0 when reverse charge). */
	vat: CartVatRate,
	/** The variant's size label, as the catalog's (`ShopVariant.sizeLabel`); `null` with no size or fit. */
	sizeLabel: z.string().nullable(),
	/**
	 * `unitPrice` over the variant's pieces per pack (a bundle's summed), in cents rounded half up, on
	 * `priceDisplay`: "€0,35 per doek". `null` for a product without pieces.
	 */
	pricePerPiece: Cents.nullable(),
	/** What one piece is called ("doek"); `null`: not said. */
	pieceNoun: z.string().nullable(),
	/** The product's main photo, as in the catalog's list; `null` without photos. */
	photo: ShopPhoto.nullable(),
});
export type CartLine = z.infer<typeof CartLine>;

/**
 * Why a discount code does not apply, with the text the shop shows ({…} filled in, amounts as
 * "€ 35,00"):
 *
 * - `notFound`: "Deze kortingscode kennen we niet." (also a code that was deleted meanwhile)
 * - `notYetValid`: "Deze kortingscode is nog niet geldig."
 * - `expired`: "Deze kortingscode is verlopen."
 * - `usedUp`: "Deze kortingscode is al zo vaak gebruikt als kan."
 * - `usedUpByCustomer`: "Je hebt deze kortingscode al gebruikt."
 * - `customerGroupNotEligible`: "Deze kortingscode geldt niet voor jouw account."
 * - `noEligibleProducts`: "Deze kortingscode geldt niet voor de producten in je mand."
 * - `belowMinimumOrderAmount`: "Deze kortingscode geldt vanaf een bestelling van {minimum}."
 */
export const CartDiscountCodeRejection = z.enum([
	"notFound",
	"notYetValid",
	"expired",
	"usedUp",
	"usedUpByCustomer",
	"customerGroupNotEligible",
	"noEligibleProducts",
	"belowMinimumOrderAmount",
]);
export type CartDiscountCodeRejection = z.infer<typeof CartDiscountCodeRejection>;

/**
 * Something the shop should tell the customer about this answer:
 *
 * - `variantRemoved`: a line's variant (or its product) is no longer for sale; the line is gone.
 * - `quantityExceedsAvailability`: a line asks for more than is available now.
 * - `quantityReduced`: merging a guest cart would have gone over what is available; the line holds
 *   what is.
 * - `cartsMerged`: the guest cart was merged into the signed-in customer's.
 * - `countryNotDeliverable`: the shop does not deliver to the cart's country for this customer;
 *   checkout will refuse until another country is chosen. Shop text: "We bezorgen helaas niet in
 *   {land}. Kies een ander land."
 * - `discountCodeRemoved`: the code on the cart no longer applies and was taken off; `reason` says
 *   why, with the shop texts at `CartDiscountCodeRejection`.
 */
export const CartNotice = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("variantRemoved"), variantId: z.string(), productName: z.string() }),
	z.object({
		kind: z.literal("quantityExceedsAvailability"),
		variantId: z.string(),
		available: z.number().int(),
	}),
	z.object({ kind: z.literal("quantityReduced"), variantId: z.string(), quantity: z.number().int() }),
	z.object({ kind: z.literal("cartsMerged") }),
	z.object({ kind: z.literal("countryNotDeliverable"), country: DeliveryCountry }),
	z.object({
		kind: z.literal("discountCodeRemoved"),
		code: z.string(),
		reason: CartDiscountCodeRejection,
	}),
]);
export type CartNotice = z.infer<typeof CartNotice>;

/**
 * - `none`: the cart has no lines, so there is nothing to ship.
 * - `shippable`: `cost` is the shipping before a free-shipping code, on `priceDisplay`: `rate`, or
 *   0 once the goods after discount reach `freeFrom` ("gratis vanaf"). `remainingForFree` is how
 *   much more makes it free ("Nog € 5,01 tot gratis verzending"); `null` when it is free already or
 *   the rule is never free. `freeByCode`: a free-shipping code takes the cost off.
 * - `notDeliverable`: no shipping rule for the country (with a `countryNotDeliverable` notice).
 */
export const CartShipping = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("none") }),
	z.object({
		kind: z.literal("shippable"),
		cost: Cents,
		rate: Cents,
		freeFrom: Cents.nullable(),
		remainingForFree: Cents.nullable(),
		freeByCode: z.boolean(),
		/** The shipping line taxed at the highest rate among the lines (0 under reverse charge). */
		vat: CartVatRate,
	}),
	z.object({ kind: z.literal("notDeliverable"), country: DeliveryCountry }),
]);
export type CartShipping = z.infer<typeof CartShipping>;

/** The code on the cart: as stored (upper case), its kind, and what it takes off now. */
export const CartDiscountCode = z.object({
	code: z.string(),
	kind: DiscountCodeKind,
	/** Off the goods and off the shipping, on `priceDisplay`. */
	goods: Cents,
	shipping: Cents,
});
export type CartDiscountCode = z.infer<typeof CartDiscountCode>;

export const CartTotals = z.object({
	/** The goods (every line but a sold-out one), before discount, on `priceDisplay`. */
	goods: Cents,
	/** What the discount code takes off the goods and shipping, on `priceDisplay`. `null`: no code. */
	discount: Cents.nullable(),
	/**
	 * The shipping cost on `priceDisplay`, before a free-shipping code (which shows in `discount`).
	 * `null`: no shipping line, because the cart is empty or cannot be delivered (`Cart.shipping`).
	 */
	shipping: Cents.nullable(),
	/** One row per VAT rate charged, highest first. Sums to the totals below. */
	vatRates: z.array(CartVatRate),
	totalExcl: Cents,
	totalVat: Cents,
	totalIncl: Cents,
	/** What the customer pays: `totalIncl`. */
	toPay: Cents,
});
export type CartTotals = z.infer<typeof CartTotals>;

export const Cart = z.object({
	/** True once the cart exists (the first line was added). */
	exists: z.boolean(),
	/** Whether a signed-in customer's cart answered. */
	signedIn: z.boolean(),
	/** Where it is delivered: decides VAT (reverse charge) and later shipping. */
	country: DeliveryCountry,
	vatTreatment: VatTreatment,
	/** The invoice will say "btw verlegd". */
	reverseCharge: z.boolean(),
	/** The side of VAT the unit prices and line totals are on: consumers incl, business excl. */
	priceDisplay: VatBasis,
	lines: z.array(CartLine),
	/** How the cart is shipped to `country`. */
	shipping: CartShipping,
	/** The code on the cart, which applies to it; `null` without one. */
	discountCode: CartDiscountCode.nullable(),
	totals: CartTotals,
	notices: z.array(CartNotice),
});
export type Cart = z.infer<typeof Cart>;

export const AddCartLineInput = z.object({ variantId: z.string().min(1), quantity: CartQuantity });
export type AddCartLineInput = z.infer<typeof AddCartLineInput>;

/** A line's place in the path: `/shop/cart/lines/{variantId}`. */
export const CartLinePath = z.object({ variantId: z.string().min(1) });

export const UpdateCartLineBody = z.object({ quantity: CartQuantity });
export type UpdateCartLineBody = z.infer<typeof UpdateCartLineBody>;

export const UpdateCartInput = z.object({ country: DeliveryCountry });
export type UpdateCartInput = z.infer<typeof UpdateCartInput>;

export const ApplyCartDiscountCodeInput = z.object({ code: z.string().trim().min(1).max(64) });
export type ApplyCartDiscountCodeInput = z.infer<typeof ApplyCartDiscountCodeInput>;

// ---------------------------------------------------------------------------------------------
// Procedures (public: a guest or a signed-in customer)

export const CartContract = {
	/** The cart of this browser or signed-in customer; an empty one when there is none yet. */
	get: oc.route({ method: "GET", path: "/shop/cart" }).output(Cart),
	/**
	 * Adds units of a variant (to the line it already has). Makes the cart, and sets its cookie, on
	 * the first line. Errors: `CartVariantNotForSale` (404: unknown, deleted or inactive),
	 * `CartQuantityUnavailable` (409: more than is available; says how many are),
	 * `CartLineLimitExceeded` (409: the line would hold more than `CART_QUANTITY_MAX`; says `max`).
	 */
	addLine: oc.route({ method: "POST", path: "/shop/cart/lines" }).input(AddCartLineInput).output(Cart),
	/**
	 * Sets a line's quantity. Errors: `CartLineNotFound` (404), `CartQuantityUnavailable` (409);
	 * lowering a quantity is always allowed.
	 */
	updateLine: oc
		.route({ method: "PUT", path: "/shop/cart/lines/{variantId}" })
		.input(CartLinePath.extend(UpdateCartLineBody.shape))
		.output(Cart),
	/** Removes a line; a line that is not there changes nothing. */
	removeLine: oc
		.route({ method: "DELETE", path: "/shop/cart/lines/{variantId}" })
		.input(CartLinePath)
		.output(Cart),
	/** Sets the delivery country. Makes the cart if there is none. */
	update: oc.route({ method: "PATCH", path: "/shop/cart" }).input(UpdateCartInput).output(Cart),
	/** Empties the cart. */
	clear: oc.route({ method: "DELETE", path: "/shop/cart" }).output(Cart),
	/**
	 * Puts a discount code on the cart, in any case, replacing the one it had. Checked now against
	 * the cart: validity, uses (in total, and by this customer when signed in), customer group,
	 * products and minimum order amount. Makes the cart if there is none. Errors:
	 * `CartDiscountCodeNotFound` (404, "Deze kortingscode kennen we niet."),
	 * `CartDiscountCodeNotApplicable` (409, with `reason` and the shop's text as `message`; see
	 * `CartDiscountCodeRejection`). A refused code leaves the cart as it was.
	 */
	applyDiscountCode: oc
		.route({ method: "PUT", path: "/shop/cart/discount-code" })
		.input(ApplyCartDiscountCodeInput)
		.output(Cart),
	/** Takes the discount code off the cart; without one, changes nothing. */
	removeDiscountCode: oc.route({ method: "DELETE", path: "/shop/cart/discount-code" }).output(Cart),
};
